import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ExpenseClaimService } from '../../services/expense-claim.service';
import { ExpensePolicyService } from '../../services/expense-policy.service';
import { ExpenseClaimDTO, ExpensePolicyDTO } from '../../interfaces/expense.interface';
import { useToast } from '../../context/ToastContext';
import { useEmployeeNames } from '../../hooks/useEmployeeNames';
import {
    CLAIM_STATUS_COLOR, claimRef, claimStatusLabel, formatAmount, isFinalApprovalTier,
} from '../../utils/expenseDisplay';
import ScrollableTable from '../common/ScrollableTable';
import ExpenseClaimApprovalDialog from './ExpenseClaimApprovalDialog';
import ExpensePayDialog from './ExpensePayDialog';
import ExpenseReceiptLink from './ExpenseReceiptLink';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatDate } from '../../utils/dateUtils';
import { departmentNameOf, useDepartmentOptions } from '../costcentre/DepartmentPicker'; // polish 4
import { projectLabel, projectTagLabel, useProjectOptions } from '../project/ProjectSelect'; // P1-A project
import { AuthService } from '../../services/auth.service';

/**
 * Someone else's claim, read from the reviewer side: reached from All Claims (EXPENSE_CLAIM_VIEW_ALL)
 * and Pending Approval. The backend authorises the read (view-all holder, or an approver whose tier
 * the claim currently sits at). Approve/reject is offered only when the claim is on the caller's
 * actionable pending list -- the same list the server filters by tier role.
 */
const ExpenseClaimReview: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const canPay = usePermission(PERMISSIONS.EXPENSE_CLAIM_PAY);
    const canViewAll = usePermission(PERMISSIONS.EXPENSE_CLAIM_VIEW_ALL);
    const departmentOptions = useDepartmentOptions(AuthService.getSelectedInstitution()); // polish 4
    const projectOptions = useProjectOptions(AuthService.getSelectedInstitution()); // P1-A project
    const projectName = (id?: string | null, code?: string | null, name?: string | null) => { const p = id ? projectOptions.find(o => o.id === id) : undefined; return p ? projectLabel(p) : (code ? projectTagLabel(code, name) : (id ? id.slice(0, 8) : '—')); };
    const [claim, setClaim] = useState<ExpenseClaimDTO | null>(null);
    const employeeName = useEmployeeNames(claim ? [claim] : []);
    const [policy, setPolicy] = useState<ExpensePolicyDTO | null>(null);
    const [actionable, setActionable] = useState(false);
    const [loading, setLoading] = useState(true);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [payOpen, setPayOpen] = useState(false);

    const load = async (claimId: string) => {
        setLoading(true);
        try {
            const [c, pending, pol] = await Promise.all([
                ExpenseClaimService.getById(claimId),
                ExpenseClaimService.listPendingApproval(0, 200).catch(() => ({ content: [] as ExpenseClaimDTO[] })),
                ExpensePolicyService.getByInstitution().catch(() => null),
            ]);
            setClaim(c);
            setPolicy(pol);
            setActionable(pending.content.some(p => p.id === claimId));
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load claim');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) load(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }
    if (!claim) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Claim not found, or you are not allowed to view it.</Typography>
                <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate(-1)}>Back</Button>
            </Box>
        );
    }

    const canPayNow = canPay && claim.status === 'APPROVED' && claim.reimbursementMethod === 'STANDALONE';

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1100 }} data-help-page="expense.claimReview.page">
            <Stack direction="row" useFlexGap flexWrap="wrap" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h5">{claim.title}</Typography>
                <Typography variant="body2" color="text.secondary">{claimRef(claim.id)}</Typography>
                <Chip label={claimStatusLabel(claim.status)} color={CLAIM_STATUS_COLOR[claim.status]} />
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" useFlexGap flexWrap="wrap" spacing={4}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Employee</Typography>
                        <Typography>{employeeName(claim)}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Period</Typography>
                        <Typography>{formatDate(claim.periodFrom)} → {formatDate(claim.periodTo)}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Total</Typography>
                        <Typography variant="h6">{formatAmount(claim.totalAmount)}</Typography>
                    </Box>
                    {claim.currentApprovalTier != null && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">Current tier</Typography>
                            <Typography>{claim.currentApprovalTier}</Typography>
                        </Box>
                    )}
                    {claim.netReimbursable != null && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">Net reimbursable</Typography>
                            <Typography variant="h6">{formatAmount(claim.netReimbursable)}</Typography>
                        </Box>
                    )}
                </Stack>
            </Paper>

            {/* W3-U */}
            {(claim.limitWarnings ?? []).length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }} data-testid="limit-warnings">
                    {(claim.limitWarnings ?? []).map((w, i) => <div key={i}>{w.message}</div>)}
                </Alert>
            )}

            <TableContainer component={Paper} data-help-section="expense.claimReview.lines">
                <ScrollableTable>
                    <Table size="small" sx={{ '& th, & td': { px: { xs: 0.75, sm: 2 } }, '& td:first-of-type': { whiteSpace: 'nowrap' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="expense.claimLines.col.date">Date</TableCell>
                                <TableCell data-help="expense.claimLines.col.category">Category</TableCell>
                                <TableCell data-help="expense.claimLines.col.description">Description</TableCell>
                                {claim.lines.some(l => l.departmentId) && <TableCell data-help="expense.claimLines.col.department">Department</TableCell>}
                                {claim.lines.some(l => l.projectId) && <TableCell data-help="expense.claimLines.col.project">Project</TableCell>}
                                <TableCell align="right" data-help="expense.claimLines.col.amount">Amount</TableCell>
                                <TableCell data-help="expense.claimLines.col.receipt">Receipt</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {claim.lines.map(l => (
                                <TableRow key={l.id}>
                                    <TableCell data-help="expense.claimLines.col.date">{formatDate(l.expenseDate)}</TableCell>
                                    <TableCell data-help="expense.claimLines.col.category">{l.categoryName ?? '—'}</TableCell>
                                    <TableCell data-help="expense.claimLines.col.description" sx={{ whiteSpace: 'normal', minWidth: 140 }}>{l.description ?? '—'}
                                    {l.lineKind && l.lineKind !== 'STANDARD' && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {l.lineKind === 'PER_DIEM' ? `Per diem: ${l.quantity ?? ''} day(s) x ${l.rate ?? ''}`
                                                : l.lineKind === 'MILEAGE' ? `Mileage: ${l.quantity ?? ''} km x ${l.rate ?? ''} ${l.vehicleType ?? ''} ${l.routeText ?? ''}`
                                                    : 'Paid by corporate card'}
                                        </Typography>
                                    )}</TableCell>
                                    {claim.lines.some(x => x.departmentId) && <TableCell data-testid="line-department" data-help="expense.claimLines.col.department">{departmentNameOf(departmentOptions, l.departmentId)}</TableCell>}
                                    {claim.lines.some(x => x.projectId) && <TableCell data-testid="line-project" data-help="expense.claimLines.col.project">{projectName(l.projectId, l.projectCode, l.projectName)}</TableCell>}
                                    <TableCell align="right" data-help="expense.claimLines.col.amount">{formatAmount(l.amount)}</TableCell>
                                    <TableCell data-help="expense.claimLines.col.receipt"><ExpenseReceiptLink claimId={claim.id} receiptId={l.receiptDocumentId} /></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </ScrollableTable>
            </TableContainer>

            <Stack direction="row" useFlexGap flexWrap="wrap" spacing={2} sx={{ mt: 3 }}>
                <Button variant="outlined" data-help="expense.claimReview.back" onClick={() => navigate(-1)}>Back</Button>
                {actionable && (
                    <Button variant="contained" data-help="expense.claimReview.review" onClick={() => setReviewOpen(true)}>
                        Review (approve / reject)
                    </Button>
                )}
                {canPayNow && (
                    <Button color="success" variant="contained" data-help="expense.claimReview.payNow" onClick={() => setPayOpen(true)}>Pay now</Button>
                )}
            </Stack>

            {reviewOpen && (
                <ExpenseClaimApprovalDialog
                    open={reviewOpen}
                    claim={claim}
                    isFinalTier={isFinalApprovalTier(policy, claim)}
                    onClose={() => setReviewOpen(false)}
                    onResolved={() => {
                        setReviewOpen(false);
                        // A tier approver without view-all loses read access once the claim moves past
                        // their tier, so go back to the queue instead of re-reading it.
                        if (canViewAll && id) load(id);
                        else navigate('/expense/pending-approval');
                    }}
                />
            )}
            <ExpensePayDialog
                open={payOpen}
                claim={claim}
                onClose={() => setPayOpen(false)}
                onPaid={next => setClaim(next)}
            />
        </Box>
    );
};

export default ExpenseClaimReview;
